import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  AlbumFrameRow,
  getAlbumFrames,
  setAlbumFeedOrder,
  updateAlbumFrameTags,
} from '../services/marketingApi';
import FrameModal, {
  ALBUM_URL,
  FAMILY_LABELS,
  FrameThumb,
  LoadPill,
  familyOf,
  fmtDur,
} from '../components/Album/FrameModal';
import { Empty, Loading, PageFrame, Pill, Section, Th, btn, btnTone, td, trClick } from '../components/Kit';

const moveBtn = 'w-7 h-6 grid place-items-center rounded-md text-[11px] leading-none text-graphite hover:text-forest hover:bg-sage/25 disabled:opacity-20 disabled:hover:bg-transparent disabled:hover:text-graphite';
const rowBtn = 'w-8 h-8 grid place-items-center rounded-lg text-[15px] leading-none text-graphite transition-colors';

const Row: React.FC<{
  f: AlbumFrameRow;
  index: number | null; // null = bench row (no arrows, no position)
  count: number;
  flash: boolean;
  onMove: (dir: -1 | 1) => void;
  onOpen: () => void;
  onRemove?: () => void; // feed rows: drop to the bench
  onAdd?: () => void;    // bench rows: append to the feed
}> = ({ f, index, count, flash, onMove, onOpen, onRemove, onAdd }) => {
  const inFeed = index != null;
  const meta = [FAMILY_LABELS[familyOf(f.code)], fmtDur(f.duration_s), f.audio].filter(Boolean).join(' · ');
  return (
    <tr onClick={onOpen} className={`${trClick(flash)} group`}>
      <td className="pl-3 py-2 w-[76px]">
        <div className="flex items-center gap-1.5">
          {inFeed ? (
            <span className="flex flex-col" onClick={(e) => e.stopPropagation()}>
              <button type="button" aria-label={`move ${f.code} up`} title="Move up" disabled={index === 0}
                      onClick={() => onMove(-1)} className={moveBtn}>▲</button>
              <button type="button" aria-label={`move ${f.code} down`} title="Move down" disabled={index === count - 1}
                      onClick={() => onMove(1)} className={moveBtn}>▼</button>
            </span>
          ) : <span className="w-7" />}
          <span className="w-7 text-center text-[15px] font-semibold num text-charcoal">{inFeed ? index + 1 : <span className="text-graphite/50">–</span>}</span>
        </div>
      </td>
      <td className="px-3 py-2">
        <div className="flex items-center gap-3 min-w-0">
          <FrameThumb f={f} />
          <div className="min-w-0 max-w-[440px]">
            <div className="flex items-center gap-2 min-w-0">
              <span className="flex-none font-mono text-[11.5px] text-graphite">{f.code}</span>
              <span className="truncate text-[14px] font-semibold text-charcoal">{f.title ?? f.type}</span>
              {f.cta && <Pill tone="gold">Book-a-visit</Pill>}
            </div>
            <div className="mt-0.5 truncate text-[12px] text-graphite">{meta || f.caption}</div>
            {f.tags.length > 0 && (
              <div className="mt-1 flex flex-wrap gap-1">
                {f.tags.slice(0, 3).map((t) => <Pill key={t}>{t}</Pill>)}
                {f.tags.length > 3 && <Pill>+{f.tags.length - 3}</Pill>}
              </div>
            )}
          </div>
        </div>
      </td>
      <td className={`${td} text-right font-semibold`}>{f.stats.views.toLocaleString('en-IN')}</td>
      <td className={`${td} text-right`}>{f.stats.unique_leads}</td>
      <td className={`${td} text-right`}>{f.stats.likes}</td>
      <td className={`${td} text-right`}>{f.stats.shares}</td>
      <td className={`${td} text-right`}><LoadPill f={f} /></td>
      <td className="pr-3 w-12">
        {onRemove ? (
          <button type="button" aria-label={`remove ${f.code} from feed`} title="Remove from feed (to the bench)"
                  onClick={(e) => { e.stopPropagation(); onRemove(); }}
                  className={`${rowBtn} hover:text-brick hover:bg-brick/5`}>✕</button>
        ) : onAdd ? (
          <button type="button" aria-label={`add ${f.code} to feed`} title="Add to end of feed"
                  onClick={(e) => { e.stopPropagation(); onAdd(); }}
                  className={`${rowBtn} hover:text-forest hover:bg-sage/25`}>+</button>
        ) : null}
      </td>
    </tr>
  );
};

const Head: React.FC = () => (
  <thead><tr>
    <Th left>#</Th><Th left>Frame</Th><Th>Views</Th><Th>Leads</Th><Th>Likes</Th><Th>Shares</Th><Th>Load</Th><Th />
  </tr></thead>
);

const AlbumOrder: React.FC = () => {
  const [frames, setFrames] = useState<AlbumFrameRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [order, setOrder] = useState<string[]>([]);
  const [baseline, setBaseline] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);
  const [flash, setFlash] = useState<string | null>(null);
  const [toast, setToast] = useState(false);
  const [open, setOpen] = useState<AlbumFrameRow | null>(null);
  const flashTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  useEffect(() => {
    getAlbumFrames()
      .then((r) => {
        setFrames(r.frames);
        const feed = r.frames
          .filter((f) => f.position != null)
          .sort((a, b) => a.position! - b.position!)
          .map((f) => f.code);
        setOrder(feed);
        setBaseline(feed);
      })
      .finally(() => setLoading(false));
  }, []);

  const byCode = useMemo(() => new Map(frames.map((f) => [f.code, f])), [frames]);
  const bench = useMemo(
    () => frames.filter((f) => !order.includes(f.code)).sort((a, b) => a.code.localeCompare(b.code)),
    [frames, order],
  );
  const changes = useMemo(() => {
    const base = new Set(baseline);
    const cur = new Set(order);
    const added = order.filter((c) => !base.has(c)).length;
    const removed = baseline.filter((c) => !cur.has(c)).length;
    // moved = frames in both lists whose relative order changed
    const kept = order.filter((c) => base.has(c));
    const keptBase = baseline.filter((c) => cur.has(c));
    const moved = kept.filter((c, i) => keptBase[i] !== c).length;
    const parts = [
      moved && `${moved} moved`,
      removed && `${removed} removed`,
      added && `${added} added`,
    ].filter(Boolean).join(' · ');
    return { any: order.join(',') !== baseline.join(','), parts };
  }, [order, baseline]);

  const flashRow = (code: string) => {
    setFlash(code);
    clearTimeout(flashTimer.current);
    flashTimer.current = setTimeout(() => setFlash(null), 600);
  };

  const removeFromFeed = (code: string) => setOrder((prev) => prev.filter((c) => c !== code));

  const addToFeed = (code: string) => {
    setOrder((prev) => (prev.includes(code) ? prev : [...prev, code]));
    flashRow(code);
  };

  const move = (code: string, dir: -1 | 1) => {
    setOrder((prev) => {
      const i = prev.indexOf(code);
      const j = i + dir;
      if (j < 0 || j >= prev.length) return prev;
      const next = [...prev];
      [next[i], next[j]] = [next[j], next[i]];
      return next;
    });
    flashRow(code);
  };

  const save = async () => {
    setSaving(true);
    try {
      await setAlbumFeedOrder(order);
      setBaseline(order);
      setFrames((prev) => prev.map((f) => {
        const i = order.indexOf(f.code);
        return { ...f, position: i === -1 ? null : i + 1 };
      }));
      setToast(true);
      setTimeout(() => setToast(false), 2500);
    } finally {
      setSaving(false);
    }
  };

  const saveTags = async (f: AlbumFrameRow, tags: string[]) => {
    setFrames((prev) => prev.map((x) => (x.code === f.code ? { ...x, tags } : x)));
    setOpen((prev) => (prev && prev.code === f.code ? { ...prev, tags } : prev));
    await updateAlbumFrameTags(f.code, tags);
  };

  const status = changes.any
    ? <Pill tone="gold">Unsaved · {changes.parts}</Pill>
    : toast ? <Pill tone="ok">Saved · live album updated</Pill> : null;

  return (
    <PageFrame
      title="Album Order"
      back={{ to: '/album', label: 'Project Album' }}
      subline={<span className="inline-flex flex-wrap items-center gap-2">
        <span className="num">{order.length} of {frames.length} frames in feed</span>
        <span>·</span>
        <a href={ALBUM_URL} target="_blank" rel="noopener noreferrer" className="hover:text-forest">live album ↗</a>
      </span>}
      action={status}
    >
      {loading ? <Loading what="album" /> : <>
        <Section title="Feed" note="Top = first frame a visitor sees">
          <table className="w-full min-w-[880px] border-collapse">
            <Head />
            <tbody>
              {order.length === 0 && <Empty cols={8}>The feed is empty.</Empty>}
              {order.map((code, i) => {
                const f = byCode.get(code)!;
                return (
                  <Row key={code} f={f} index={i} count={order.length} flash={flash === code}
                       onMove={(dir) => move(code, dir)} onOpen={() => setOpen(f)} onRemove={() => removeFromFeed(code)} />
                );
              })}
            </tbody>
          </table>
        </Section>

        {bench.length > 0 && (
          <Section title="Bench" note="Not in the feed · + adds to the end">
            <table className="w-full min-w-[880px] border-collapse">
              <Head />
              <tbody>
                {bench.map((f) => (
                  <Row key={f.code} f={f} index={null} count={0} flash={false} onMove={() => undefined}
                       onOpen={() => setOpen(f)} onAdd={() => addToFeed(f.code)} />
                ))}
              </tbody>
            </table>
          </Section>
        )}

        {changes.any && (
          <div className="sticky bottom-0 z-20 -mx-10 px-10 py-3.5 border-t border-charcoal/10 bg-warm-white/95 backdrop-blur">
            <div className="flex flex-wrap items-center gap-3">
              <span className="w-2 h-2 rounded-full bg-gold" aria-hidden />
              <span className="text-[13.5px] text-charcoal"><b>{changes.parts}</b> <span className="text-graphite">· goes live on save</span></span>
              <span className="flex-1" />
              <button type="button" onClick={() => setOrder(baseline)} disabled={saving} className={`${btn} ${btnTone.charcoal}`}>Discard</button>
              <button type="button" onClick={save} disabled={saving} className={`${btn} ${btnTone.forest}`}>{saving ? 'Saving…' : 'Save order'}</button>
            </div>
          </div>
        )}
      </>}

      {open && <FrameModal key={open.code} frame={open} onClose={() => setOpen(null)} onSaveTags={saveTags} />}
    </PageFrame>
  );
};

export default AlbumOrder;
